import { useState } from 'react'
import { Smartphone, ShieldAlert, Fingerprint } from 'lucide-react'
import {
  startAuthentication,
  browserSupportsWebAuthn,
} from '@simplewebauthn/browser'
import type { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser'
import { useI18n } from '../i18n/useI18n'
import { showToast } from './Toast'
import { api } from '../services/api'
import { markStoreDeviceApproved, recheckStoreDeviceApproval } from '../hooks/useStoreDeviceApprovalGate'

interface Props {
  open: boolean
  denied: boolean
  /** 手動再確認 (4s poll しているので無くても自動解除はされる). */
  onRecheck: () => void
}

/**
 * 店舗オーナー向け初見デバイス承認 待機 モーダル (#867 v3+).
 *
 * 顧客側 `DeviceApprovalWaitingModal` (#819) と同様に未信頼新端末を全画面
 * block するが、**passkey 認証で即解除**するボタンを追加 (refined-(a))。
 * 端末バインド passkey を持っているオーナーは trusted 端末経由の承認待ち
 * を回避できる(WebAuthn 推奨ポジショニングの実用価値の核)。
 *
 * passkey assertion 成功 → backend が refined-(a) で device-id を自動
 * trusted 化 → markStoreDeviceApproved() で楽観的に modal unmount。
 * 失敗 / passkey 未登録 → トースト → user は trusted 端末からの承認待ち
 * (= 顧客側と同じ通常導線)に戻る。
 */
export default function StoreDeviceApprovalWaitingModal({ open, denied, onRecheck }: Props) {
  const { t } = useI18n()
  const [passkeyBusy, setPasskeyBusy] = useState(false)
  const supported =
    typeof window !== 'undefined' && browserSupportsWebAuthn()

  if (!open) return null

  const verifyWithPasskey = async () => {
    if (passkeyBusy) return
    setPasskeyBusy(true)
    try {
      const { options, challengeId } = await api.storeWebauthnAuthBegin()
      const credential = await startAuthentication({
        optionsJSON: options as PublicKeyCredentialRequestOptionsJSON,
      })
      const result = await api.storeWebauthnAuthComplete(challengeId, credential)
      if (result.deviceTrusted === true) {
        // refined-(a) で trusted 化を backend が確認 → modal unmount。
        // 念のため recheck も発火させて backend 状態と同期する。
        markStoreDeviceApproved()
        recheckStoreDeviceApproval()
        showToast({ type: 'success', message: t('storeDeviceApproval.passkeyVerified') })
      } else {
        // #1105: assertion は本物だが復旧が成立しなかった場合。
        // **#1934 で同期 passkey は成立するようになった**ので、ここに来るのは
        // 「否認・解除された端末」が主。同期を理由にした案内は嘘になるため、
        // 実態に合わせた文言に差し替えてある。
        showToast({ type: 'info', message: t('storeDeviceApproval.passkeyBlocked') })
      }
    } catch (e) {
      const name = (e as { name?: string })?.name
      if (name === 'NotAllowedError' || name === 'AbortError') {
        showToast({ type: 'info', message: t('storeSettings.webauthnCancelled') })
      } else {
        console.error('[StoreDeviceApproval] passkey verify failed:', e)
        showToast({ type: 'error', message: t('storeDeviceApproval.passkeyFailed') })
      }
    } finally {
      setPasskeyBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 px-4 animate-fade-in">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 animate-scale-in">
        <div
          className={`flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full ${
            denied ? 'bg-amber-100' : 'bg-navy/10'
          }`}
        >
          {denied ? (
            <ShieldAlert size={32} className="text-amber-600" />
          ) : (
            <Smartphone size={32} className="text-navy" />
          )}
        </div>

        <h3 className="font-display text-xl font-bold text-text-primary text-center mb-2">
          {t(denied ? 'deviceApproval.deniedTitle' : 'deviceApproval.waitTitle')}
        </h3>
        <p className="font-body text-sm text-text-secondary text-center mb-5 leading-relaxed">
          {t(denied ? 'deviceApproval.deniedDescription' : 'deviceApproval.waitDescription')}
        </p>

        {!denied && (
          <>
            <div className="flex items-center justify-center gap-2 mb-5">
              <span className="inline-block w-2 h-2 rounded-full bg-navy animate-pulse" />
              <span className="font-body text-xs text-text-secondary">
                {t('deviceApproval.waitChecking')}
              </span>
            </div>

            {/* #867 v3+: passkey で即解除導線 (refined-(a))。
                browser 非対応なら button 非表示。WebAuthn 未登録の owner には
                失敗トーストで通常の trusted-device 承認待ちに戻る挙動。 */}
            {supported && (
              <button
                onClick={verifyWithPasskey}
                disabled={passkeyBusy}
                className="w-full h-12 rounded-xl font-display font-bold text-sm bg-matcha text-white tap-effect transition-colors disabled:opacity-50 mb-2 flex items-center justify-center gap-2"
              >
                <Fingerprint size={18} />
                {passkeyBusy
                  ? t('storeDeviceApproval.passkeyBusy')
                  : t('storeDeviceApproval.passkeyVerifyCta')}
              </button>
            )}

            <button
              onClick={onRecheck}
              className="w-full h-12 rounded-xl font-display font-bold text-sm bg-yokoso-red text-white tap-effect transition-colors"
            >
              {t('deviceApproval.recheckAction')}
            </button>
          </>
        )}

        <p className="font-body text-[11px] text-text-secondary text-center mt-4 leading-relaxed">
          {t('deviceApproval.waitRecoveryHint')}
        </p>
      </div>
    </div>
  )
}
